import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BreadCrumb, { Page } from '../../components/BreadCrumb';
import formatPrice from '../../services/utils/formatPrice';
import { FiFileText } from 'react-icons/fi';
import { getContractsFromDealOrDebt } from '../../utils/getContractsToShow';
import { BiCalendar } from 'react-icons/bi';
import getOriginalContract from '../../utils/getOriginalContract';
import Skeleton from 'react-loading-skeleton';
import { useSelector, useDispatch } from 'react-redux';
import Modal from './WaitingPaymentModal';
import NegotiatioInfoCard from '../../components/NegotiationInfoCard';
import { HandlerNextAction } from '../AbstractNegotiation/AbstractNegotiationService';
import CreditCardForm, {
  CreditCardFormRef,
} from '../../components/CreditCardPaymentForm';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  Wrapper,
  Container2,
  TitleWrapper,
  IconBack,
  Title,
  SubWrapper,
  Subtitle,
  DetailContent,
  Subtitle2,
  SkeletonWrapper,
  Button,
  IconAlert,
  Subtitle3,
  PaymentCard,
  IconPix,
  IconBarCode,
  IconCreditCard,
} from './styles';
import { navigationUrl } from '../../store/analytic/actions';

type NegotiationProps = {
  contract: GQL.IDebt;
};

const paymentMethodList = [
  {
    icon: 'pix',
    text: 'Pagar com PIX',
    code: 1,
  },
  {
    icon: 'boleto',
    text: 'Pagar com Boleto',
    code: 0,
  },
  {
    icon: 'cartão',
    text: 'Pagar com Cartão de crédito',
    code: 2,
  },
];

const getIcon = (code: number) => {
  switch (code) {
    case 1:
      return <IconPix />;
    case 0:
      return <IconBarCode />;
    case 2:
      return <IconCreditCard />;
    default:
      return;
  }
};

const AgreementSummary = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const creditCardFormRef = useRef<CreditCardFormRef>(null);
  const location = useLocation<NegotiationProps & { paymentType: number }>();
  const contract: GQL.IDebt = location.state && location.state.contract;
  const paymentType: number = location.state && location.state.paymentType;
  const contractsToShow = useMemo(() => getContractsFromDealOrDebt(contract), [
    contract,
  ]);
  const [loading, setLoading] = useState(false);

  const {
    selectedPaymentMethod,
    negotiationLoading,
    list,
  } = useSelector((state: ApplicationState) => ({
    negotiationLoading: state.negotiation.negotiationLoading,
    list: state.negotiation.list,
    selectedPaymentMethod: state.contract.selectedPaymentMethod,
    selectedInstallments: state.contract.selectedInstallments,
  }));

  const handleNavigation = async (action: string) => {
    if (action === 'previous') {
      return contract?.dealId
        ? history.replace({
          pathname: paymentType ? '/parcela-acordo' : '/métodos-pagamento',
          state: { contract, agreement: contract },
        })
        : history.goBack();
    }

    if (selectedPaymentMethod === 2) {
      const isValid = creditCardFormRef.current?.validateFields();

      if (!isValid) {
        return;
      }
    }

    const data = creditCardFormRef.current?.getValues();
    const installment = list.find(
      (item) => item.optionId === data?.installments
    );

    setLoading(true);

    await HandlerNextAction({
      contract,
      databaseId: contract.databaseId,
      negotiationOption:
        selectedPaymentMethod === 2 || paymentType === 2
          ? (installment as GQL.IOption)
          : list?.[0],
      history,
      dealInfos: {
        contrato: contract.contracts,
        opcaoID:
          selectedPaymentMethod === 2 || paymentType === 2
            ? (installment?.optionId as string)
            : (list?.[0]?.optionId as string),
        valorEntrada:
          selectedPaymentMethod === 2 || paymentType === 2
            ? installment?.firstPortionValue
            : list?.[0]?.firstPortionValue,
        valorDemais:
          selectedPaymentMethod === 2 || paymentType === 2
            ? installment?.othersPortionsValue
            : list?.[0]?.othersPortionsValue,
      },
      creditCardData: data,
      paymentType: paymentType ?? (selectedPaymentMethod as number),
    });

    setLoading(false);
  };

  const breadcrumbPreviousPages: Page[] = [
    {
      name: 'Débitos e Acordos',
      route: '/contrato',
    },
    {
      name: contract?.dealId ? 'Meu Acordo' : 'Minhas Faturas',
      route: contract?.dealId ? '/parcela-acordo' : '/faturas',
      state: { agreement: contract },
    },
    ...(!paymentType
      ? [
        {
          name: 'Pagamento',
          route: '/métodos-pagamento',
          state: { contract },
        },
      ]
      : []),
  ];

  const breadcrumbProps = {
    currentPage: 'Resumo',
    previouPages: breadcrumbPreviousPages,
    showBreadCrumbOnMinWidth: '960px',
    ...(contract?.dealId && {
      onNavigateBackTo: (index: number) => {
        const page = breadcrumbPreviousPages[index];
        history.replace(page.route, page.state);
      },
    }),
  };

  useEffect(() => {
    dispatch(
      navigationUrl.request({
        action: '/resumoAcordo',
      }),
    );
  }, [dispatch])

  return (
    <div>
      <Wrapper>
        <Container2>
          <TitleWrapper>
            <IconBack onClick={() => history.goBack()} />
            <Title>Resumo</Title>
            <BreadCrumb {...breadcrumbProps} />
          </TitleWrapper>
          {negotiationLoading ? (
            <SkeletonWrapper>
              <Skeleton className='info' />
              <Skeleton className='info' />
              <Skeleton className='option' style={{ borderRadius: '60px' }} />
              <Skeleton className='option' style={{ borderRadius: '60px' }} />
            </SkeletonWrapper>
          ) : (
            <>
              <SubWrapper>
                <Subtitle>
                  Valor atual da dívida: R${' '}
                  {contract?.updatedValue
                    ? formatPrice(contract.updatedValue)
                    : list?.[0]?.totalValue &&
                    formatPrice(list?.[0]?.totalValue)}
                </Subtitle>
                <DetailContent style={{ width: '100%' }}>
                  <div className='bank-name'>
                    <span>{contract && contract.creditorName}</span>
                  </div>
                  <div className='default'>
                    <FiFileText className='space-right' />
                    Contrato:
                    <strong className='space-left'>
                      {contractsToShow &&
                        getOriginalContract(contractsToShow[0])}
                    </strong>
                  </div>
                  {((selectedPaymentMethod !== null &&
                    selectedPaymentMethod !== undefined &&
                    selectedPaymentMethod !== 2) ||
                    (paymentType && paymentType !== 2)) && (
                      <div className='default'>
                        <BiCalendar className='space-right' />
                        Vencimento:{' '}
                        <span>
                          <strong>{list?.[0]?.formatedDueDate}</strong>
                        </span>
                      </div>
                    )}
                </DetailContent>
                {(selectedPaymentMethod !== 1 || paymentType) && (
                  <Subtitle2>
                    <IconAlert className='alert-icon' />
                    <span>
                      O valor final poderá sofrer alteração de centavos
                    </span>
                  </Subtitle2>
                )}
                {((selectedPaymentMethod !== null &&
                  selectedPaymentMethod !== undefined &&
                  selectedPaymentMethod !== 2) ||
                  (paymentType && paymentType !== 2)) && (
                    <>
                      <Subtitle3>Opção para negociar selecionada</Subtitle3>
                      <NegotiatioInfoCard negotiationInfo={list} />
                    </>
                  )}
                <Subtitle3>Opção de pagamento selecionada</Subtitle3>
                <NegotiatioInfoCard negotiationInfo={list} />
              </SubWrapper>
              <div className='footer'>
                <Button
                  color='white'
                  onClick={() => handleNavigation('previous')}
                >
                  Voltar
                </Button>
                <Button
                  color='primary'
                  onClick={() => !loading && handleNavigation('next')}
                  disabled={loading}
                >
                  {loading && <ClipLoader size={20} color='#fff' />}
                  <p>{loading ? 'Aguarde...' : 'Efetivar Acordo'}</p>
                </Button>
              </div>
            </>
          )}
        </Container2>
      </Wrapper>
      {loading && (selectedPaymentMethod === 2 || paymentType === 2) && (
        <Modal />
      )}
    </div>
  );
};

export default AgreementSummary;
